// cSpell:enableCompoundWords
import { ComponentSize } from '@eleventheye/asui';
import { ConsoleID, getConsoleState } from 'app/config/Logger';
import { Cls, Emojis } from 'app/constants';
import { useAppSelector } from 'app/hooks';
import Division from 'app/scenes/statsmanager/components/standings/components/division/Division';
import Analysis from 'app/scenes/statsmanager/components/team/components/analysis/Analysis';
import EditTeamPanel from 'app/scenes/statsmanager/components/team/components/editteampanel';
import TeamInfoSection from 'app/scenes/statsmanager/components/team/components/teaminfosection';
import useEditTeam from 'app/scenes/statsmanager/components/team/useEditTeam';
import TeamLogo from 'app/scenes/statsmanager/components/teamlogo/TeamLogo';
import { getSelectedTeamName, getSelectedWeek, getSelectedYear } from 'app/slices/GamesSlice';
import Ruler from 'components/ruler/Ruler';
import ShowByFilters from 'components/showbyfilters/ShowByFilters';
import { TeamAbrvToTeamID } from 'helpers/IdHelpers';
import { getOpponentAndGameInfo } from 'helpers/TeamHelpers';
import useDataLoaderForGameDetails from 'hooks/useDataLoaderForGameDetails';
import usePageTitle from 'hooks/usePageTitle';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { GameSubNavCategory, ITeamWeekRank } from 'types';
import ControlButtons from './components/controlbuttons/ControlButtons';
import TeamField from './components/field/TeamField';
import StatsNavBar from './components/statsnav/StatsNavBar';
import TeamStat from './components/teamstat/TeamStat';
import styles from './Team.module.scss';

type TeamProps = {
  className?: string;
  onCloseSelectedTeam?: () => void;
  selectedTeam?: string;
};

const CONSOLE_ON = getConsoleState(ConsoleID.Team);

export const Team: React.FC<TeamProps> = ({ className = '' }) => {
  const baseCls = `${styles.TeamContainer} ${className}`;

  useDataLoaderForGameDetails();

  const selectedTeam = useAppSelector(getSelectedTeamName);
  const selectedWeek = useAppSelector(getSelectedWeek);
  const selectedYear = useAppSelector(getSelectedYear);
  const { setPageTitle } = usePageTitle();

  useEffect(() => {
    setPageTitle(`${selectedYear} ${selectedWeek} ${selectedTeam} Details`);
  }, [setPageTitle, selectedTeam, selectedYear, selectedWeek]);

  const [selectedSubCategory, setSelectedSubCategory] = useState<GameSubNavCategory>();
  const [selectedRanking, setSelectedRanking] = useState<ITeamWeekRank[]>([]);

  const editHook = useEditTeam();
  const { teamStandings, teamDetails, weeklyTeamStandings, schedule, editMode, setEditMode } = editHook;

  const [showDivStandings, setShowDivStandings] = useState<boolean>(false);

  const showStandingsCls = showDivStandings || editMode ? styles.TeamSectionsRowShowTeamStandings : '';

  const onShowDivStandings = useCallback(() => {
    setShowDivStandings((prev) => !prev);
  }, []);

  useEffect(() => {
    if (CONSOLE_ON) console.log(`${Emojis.eyes} loading standings with %c 2s %c delay`, Cls.Val);
    const timerID = setTimeout(() => {
      onShowDivStandings();
    }, 2000);

    return () => clearTimeout(timerID);
  }, []);

  const onEditMode = useCallback(() => {
    setEditMode(true);
  }, [setEditMode, onShowDivStandings]);

  const renderOpposingTeamLogo = useCallback((isByeWeek: boolean, teamAbrv: string) => {
    if (isByeWeek) return <span className={styles.DivisionTeamLogo}></span>;
    return <TeamLogo className={styles.DivisionTeamLogo} teamName={teamAbrv} iconType="v1" />;
  }, []); // No dependencies - pure function

  const statAverages = useMemo(() => {
    if (!selectedRanking || selectedRanking.length === 0) {
      return { bestAvg: 0, worstAvg: 0 };
    }

    const bestValues = selectedRanking.map((cat) => cat.bestTeam);
    const worstValues = selectedRanking.map((cat) => cat.worstTeam);
    const bestAvg = bestValues.reduce((acc, curr) => acc + curr.value, 0) / bestValues.length;
    const worstAvg = worstValues.reduce((acc, curr) => acc + curr.value, 0) / worstValues.length;

    return { bestAvg, worstAvg };
  }, [selectedRanking]);

  const getTeamRecord = useCallback(() => {
    const teamID = selectedTeam ? TeamAbrvToTeamID[selectedTeam as keyof typeof TeamAbrvToTeamID] : '0';
    if (teamID === '0') return '0 - 0 - 0';
    const index = parseInt(teamID) - 1;
    const lastWeeklyStanding = weeklyTeamStandings ? weeklyTeamStandings[index] : null;
    const standings = teamStandings || lastWeeklyStanding;
    if (!standings) return '0 - 0 - 0';

    const wins = standings.wins ?? 0;
    const losses = standings.losses ?? 0;
    const ties = standings.ties ?? 0;
    return `${wins} - ${losses} - ${ties}`;
  }, [teamStandings, weeklyTeamStandings, selectedTeam]);

  const renderStatGraph = useCallback(
    (selectedRanking: ITeamWeekRank[]) => {
      if (!selectedRanking) return null;

      const { bestAvg, worstAvg } = statAverages;

      return (
        <>
          {schedule.length > 0 &&
            selectedRanking.map((cat: ITeamWeekRank) => {
              const { weekID, teamID, teamAbrv, isByeWeek } = getOpponentAndGameInfo(cat, schedule);

              if (weekID && teamID) {
                const opntIcon = renderOpposingTeamLogo(!!isByeWeek, teamAbrv ?? '');
                return (
                  <div className={styles.TeamStatsPosition} key={`${weekID}-${teamID}`}>
                    <TeamStat
                      legend="week"
                      bestAvg={bestAvg}
                      worstAvg={worstAvg}
                      category={cat}
                      showBye={isByeWeek}
                      showIcon={!isByeWeek}
                      icon={opntIcon}
                      popupEnabled
                    />
                  </div>
                );
              }

              return null;
            })}
        </>
      );
    },
    [selectedRanking, schedule, statAverages, renderOpposingTeamLogo],
  );

  return (
    <div className={baseCls}>
      {!editMode && (
        <ControlButtons
          showingDivStandings={showDivStandings}
          onShowDivStandings={onShowDivStandings}
          onEdit={onEditMode}
        />
      )}

      <div className={`${styles.TeamSectionsRow} ${showStandingsCls}`}>
        {/* ON/OFF Team's division section  */}
        {!editMode && !!teamDetails && (
          <Division
            title={teamDetails.teamDivision}
            divisionId={teamDetails.teamDivision}
            highlightTeamID={teamDetails.teamID}
            weeklyTeamStandings={weeklyTeamStandings}
            openFrom="LEFT"
          />
        )}

        {/* Team's logo and record section  */}
        <div className={styles.TeamLogoAndRecordContainer}>
          <TeamField text={teamDetails?.teamFullName} withShadow />
          <TeamLogo teamName={selectedTeam} />
          <TeamField
            className={styles.TeamRecordField}
            textSize={ComponentSize.Large}
            withShadow
            text={getTeamRecord()}
          />
        </div>

        {/* Team's EDIT/READ info/location/stadium section  */}
        <div className={styles.TeamFields}>
          {editMode ? <EditTeamPanel editHook={editHook} /> : <TeamInfoSection teamDetails={teamDetails} />}
        </div>
      </div>

      <Ruler style={{ width: '98%' }} className="STEEL" />

      <StatsNavBar onCategorySelected={setSelectedSubCategory} />
      <div className={styles.TeamStatsRow}>
        <div className={styles.TeamStatsColumn}>
          <ShowByFilters
            showSingleTeam
            showAllWeeks
            onSelectedRanking={setSelectedRanking}
            selectedSubCategory={selectedSubCategory}
          />
          <div className={styles.Row}>{renderStatGraph(selectedRanking)}</div>
        </div>
        <Analysis categoryStats={selectedRanking} className={styles.NerdMathLayout} />
      </div>
    </div>
  );
};

export default Team;
